import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Row, Col, Card, Tabs, Tab, Alert } from 'react-bootstrap';
import { CustomToast } from '@src/components/toast';
// Types
import { InvoiceData, PaymentData, CustomerSyncData } from './types';
// Components
import { PaginationComponent } from '@src/components/pagination';
// API
import { allInvoices, allPayments, allCustomers, integrationByType } from '@src/Api';
import { Container } from 'react-bootstrap';
import { AnimatePresence } from 'framer-motion';
import { InvoiceTab } from './invoiceTab';
import { PaymentTab } from './paymentTab';
import { CustomerTab } from './customerTab';

export const SyncOverviewPage: React.FC = () => {
  const [customersEnabled, setCustomersEnabled] = useState(false);
  const [customers, setCustomers] = useState<CustomerSyncData[]>([]);
  const [payments, setPayments] = useState<PaymentData[]>([]);
  const [error, setError] = useState('');
  const requestId = useRef(0);
  const [invoicesData, setInvoicesData] = useState<InvoiceData[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalResults, setTotalResults] = useState<number>(25);
  const [resultsPerPage, setResultsPerPage] = useState<number>(25);
  const [toasts, setToasts] = useState<{ id: number; message: string }[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [activeKey, setActiveKey] = useState('invoices');

  useEffect(() => {
    let active = true;
    integrationByType('dynamicsBc', 'data')
      .then((response) => {
        if (active) setCustomersEnabled(response?.status === 200 && response.data?.users_integrations_default_data?.customer_data_master === 'dynamicsBc');
      })
      .catch(() => {
        if (active) setCustomersEnabled(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const handleSelectTab = (key: string | null) => {
    if (key && key !== activeKey) {
      requestId.current++;
      setLoading(true);
      setError('');
      setPayments([]);
      setCustomers([]);
      setActiveKey(key);
      setCurrentPage(0);
      setTotalResults(0);
      setInvoicesData([]);
    }
  };

  const fetchSyncData = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError('');
    try {
      const fetchData = activeKey === 'customers' ? allCustomers : activeKey === 'payments' ? allPayments : allInvoices;
      const response = await fetchData(resultsPerPage, currentPage);
      if (id !== requestId.current) return;
      if (!response || response.status !== 200 || !Array.isArray(response.data?.results)) {
        throw new Error('Unable to load synchronization history. Please try again.');
      }
      if (activeKey === 'customers') setCustomers(response.data.results);
      else if (activeKey === 'payments') setPayments(response.data.results);
      else setInvoicesData(response.data.results);
      setTotalResults(response.data.total);
    } catch {
      if (id !== requestId.current) return;
      setError('Unable to load synchronization history. Please try again.');
      setPayments([]);
      setCustomers([]);
      setInvoicesData([]);
      setTotalResults(0);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [activeKey, currentPage, resultsPerPage]);

  useEffect(() => {
    void fetchSyncData();
    return () => {
      requestId.current++;
    };
  }, [fetchSyncData]);

  const removeToast = (id: number) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  };

  const handleDropdownChange = (value: number) => {
    setCurrentPage(0);
    setResultsPerPage(value);
  };

  return (
    <>
      <Container className="toast-b-right">
        <AnimatePresence>
          {toasts.map((toast, index) => (
            <CustomToast
              duration={3000}
              key={index}
              show={true}
              title={`Sync status for ID: ${toast.id}`}
              message={toast.message}
              type="success"
              onClose={() => removeToast(toast.id)}
            />
          ))}
        </AnimatePresence>
      </Container>
      <Row className="mt-4 sync-overview">
        <Col sm={12}>
          <Card>
            <Card.Header>Sync overview</Card.Header>
            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              <Tabs className="custom-tab" activeKey={activeKey} id="sync-overview-tabs" onSelect={handleSelectTab}>
                <Tab eventKey="invoices" title="Invoices">
                  <InvoiceTab invoiceData={invoicesData} loading={loading} setToasts={setToasts} fetchSyncData={fetchSyncData} />
                </Tab>
                <Tab eventKey="payments" title="Payments">
                  <PaymentTab payments={payments} loading={loading} onResync={fetchSyncData} />
                </Tab>
                {customersEnabled && (
                  <Tab eventKey="customers" title="Customers">
                    <CustomerTab customers={customers} loading={loading} onResync={fetchSyncData} />
                  </Tab>
                )}
              </Tabs>
            </Card.Body>
            <Row className="mt-4">
              <Col>
                <PaginationComponent
                  itemsCount={totalResults}
                  itemsPerPage={resultsPerPage}
                  currentPage={currentPage + 1}
                  setCurrentPage={(pageChangeTo) => setCurrentPage(pageChangeTo - 1)}
                />
              </Col>
              <Col className="result-dropdown">
                <select className="form-select dropdown" value={resultsPerPage} onChange={(e) => handleDropdownChange(parseInt(e.target.value))}>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </>
  );
};
